import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Download, Repeat, Search } from "lucide-react";
import PageLayout from "@/components/layout/PageLayout";
import { Button } from "@/components/ui/button";
import LedgerTable from "@/components/ledger/LedgerTable";
import { usePortfolioData } from "@/hooks/use-portfolio-data";
import { LEDGER_CATEGORIES } from "@/lib/categories";
import {
  BUSINESS,
  buildLedgerRows,
  currentMonthKey,
  filterLedgerRows,
  houseName,
  ledgerToCsv,
  monthLabel,
  shiftMonth,
} from "@/lib/ledger";

const PERIODS = [
  { value: "ytd", label: "This year" },
  { value: "12m", label: "Last 12 months" },
  { value: "all", label: "Everything" },
];

const selectClass = "h-9 rounded-lg border border-input bg-background px-3 text-sm";

export default function Ledger() {
  const [params, setParams] = useSearchParams();
  const { data, isLoading, error, refresh } = usePortfolioData(["properties", "tenants", "expenses", "rentPayments"]);
  const period = params.get("m") || currentMonthKey();
  const house = params.get("house") || "";
  const category = params.get("cat") || "";
  const search = params.get("q") || "";
  const isMonth = /^\d{4}-\d{2}$/.test(period);

  const allRows = useMemo(() => buildLedgerRows(data), [data]);
  const rows = useMemo(
    () => filterLedgerRows(allRows, { period, house, category, search }),
    [allRows, period, house, category, search],
  );
  const propertiesById = useMemo(() => new Map(data.properties.map((p) => [String(p._id), p])), [data.properties]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  function exportCsv() {
    const blob = new Blob([ledgerToCsv(rows, propertiesById)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `highlander-ledger-${period}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <PageLayout
      title="Ledger"
      actions={
        <>
          <Button variant="ghost" size="sm" asChild className="gap-1.5">
            <Link to="/finance?tab=recurring">
              <Repeat className="h-4 w-4" /> Repeats
            </Link>
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!rows.length} className="gap-1.5">
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        </>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center rounded-lg border border-input bg-background">
          <button
            type="button"
            className="grid h-9 w-9 place-items-center text-muted-foreground hover:text-foreground disabled:opacity-30"
            onClick={() => update("m", shiftMonth(isMonth ? period : currentMonthKey(), -1))}
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <select
            className="h-9 bg-transparent px-1 text-sm font-medium"
            value={period}
            onChange={(event) => update("m", event.target.value)}
            aria-label="Period"
          >
            {isMonth ? <option value={period}>{monthLabel(period)}</option> : <option value={currentMonthKey()}>{monthLabel(currentMonthKey())}</option>}
            {PERIODS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="grid h-9 w-9 place-items-center text-muted-foreground hover:text-foreground"
            onClick={() => update("m", shiftMonth(isMonth ? period : currentMonthKey(), 1))}
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <select className={selectClass} value={house} onChange={(event) => update("house", event.target.value)} aria-label="House">
          <option value="">All houses</option>
          {data.properties.map((property) => (
            <option key={property._id} value={String(property._id)}>
              {houseName(property)}
            </option>
          ))}
          <option value={BUSINESS}>Business (no house)</option>
        </select>
        <select className={selectClass} value={category} onChange={(event) => update("cat", event.target.value)} aria-label="Category">
          <option value="">All categories</option>
          {LEDGER_CATEGORIES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <label className="relative min-w-[180px] flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            className={`${selectClass} w-full pl-9`}
            value={search}
            onChange={(event) => update("q", event.target.value)}
            placeholder="Search"
            aria-label="Search the ledger"
          />
        </label>
      </div>

      {error ? (
        <p className="mb-3 text-sm text-destructive">Couldn't load the ledger: {error.message}</p>
      ) : null}

      {isLoading ? (
        <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>
      ) : (
        <LedgerTable
          rows={rows}
          properties={data.properties}
          tenants={data.tenants}
          onChanged={refresh}
          emptyMessage={
            isMonth ? (
              <>
                Nothing in {monthLabel(period)}. Type an entry above,{" "}
                <button type="button" className="underline" onClick={() => update("m", "ytd")}>
                  show the whole year
                </button>
                , or{" "}
                <Link className="underline" to="/import">
                  import your spreadsheet
                </Link>
                .
              </>
            ) : (
              "Nothing matches these filters."
            )
          }
        />
      )}
      <p className="mt-3 text-xs text-muted-foreground">
        Tip: type in the top row and press Enter — the date, house and category stay for the next line. Click any cell to edit it.
      </p>
    </PageLayout>
  );
}
